import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { DashboardPackageDistributionAPI } from '@/api/type';
import { useTranslation } from 'react-i18next';

interface MemberDistributionChartProps {
  distributionData?: DashboardPackageDistributionAPI;
}

const COLORS = [
  'var(--color-chart-blue)',
  'var(--color-chart-green)',
  'var(--color-chart-amber)',
  'var(--color-chart-purple)',
];

export const MemberDistributionChart = ({ distributionData }: MemberDistributionChartProps) => {
  const { t } = useTranslation();
  const isDarkMode = document.documentElement.classList.contains('dark');

  const totalMembers = distributionData?.data?.total_count || 0;
  const totalValue = distributionData?.data?.total_value || 0;

  const defaultPackages = [
    { name: 'PIONEER', count: 0, value: 300000000, percent: 0, items: [] },
    { name: 'TITAN', count: 0, value: 1500000000, percent: 0, items: [] },
    { name: 'TRADER', count: 0, value: 13000000, percent: 0, items: [] },
  ];

  const apiPackages = distributionData?.data?.distribution || [];

  // Use a map to merge API data into defaults and add any new packages
  const packagesMap = new Map();
  defaultPackages.forEach(p => {
    packagesMap.set(p.name, { ...p });
  });

  apiPackages.forEach(apiPkg => {
    if (packagesMap.has(apiPkg.name)) {
      const existing = packagesMap.get(apiPkg.name);
      packagesMap.set(apiPkg.name, {
        ...existing,
        ...apiPkg,
        // Ensure values remain meaningful if API returns 0 but we have a default
        value: apiPkg.value / apiPkg.count || existing.value
      });
    } else {
      packagesMap.set(apiPkg.name, apiPkg);
    }
  });

  const packages = Array.from(packagesMap.values());

  const chartData = packages.map((pkg) => ({
    name: pkg.name,
    value: pkg.count,
    percentage: pkg.percent.toFixed(1),
  }));

  const isChartEmpty = chartData.every(item => item.value === 0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value);
  };

  return (
    <div className="!bg-white dark:!bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
      <div className="p-6 border-b border-gray-200 dark:border-dark-border">
        <h2 className="text-xl font-bold !text-gray-900 dark:!text-white mb-2">
          {t('dashboard.member_distribution_by_package')}
        </h2>
        <p className="text-sm !text-gray-600 dark:!text-dark-text-secondary">
          {t('dashboard.total_packages', { count: totalMembers })}
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={isChartEmpty ? [{ name: 'Empty', value: 1 }] : chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={!isChartEmpty}
                  label={!isChartEmpty ? ({ percentage }) => `${percentage}%` : false}
                  outerRadius={110}
                  fill="#8884d8"
                  dataKey="value"
                  stroke={isChartEmpty ? 'none' : '#fff'}
                >
                  {isChartEmpty ? (
                     <Cell key="cell-empty" fill="#e0f2fe" /> 
                  ) : (
                    chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))
                  )}
                </Pie>
                {!isChartEmpty && (
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      color: isDarkMode ? '#f3f4f6' : '#111827',
                    }}
                    itemStyle={{
                      color: isDarkMode ? '#f3f4f6' : '#111827',
                    }}
                    formatter={(value: number) => t('dashboard.members_count', { count: value })}
                  />
                )}
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col justify-center space-y-2">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-md !bg-gray-50 dark:!bg-dark-card-elevated border border-gray-100 dark:border-dark-border"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <div>
                    <p className="text-sm font-semibold !text-gray-900 dark:!text-white !mb-1">
                      {pkg.name}
                    </p>
                    <p className="text-xs !text-gray-500 dark:!text-dark-text-secondary !mb-1">
                      {formatCurrency(pkg.value)} {t('dashboard.per_partner')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold !text-gray-900 dark:!text-white !mb-1">
                    {pkg.count}
                  </p>
                  <p className="text-xs !text-gray-500 dark:!text-dark-text-secondary !mb-1">
                    {pkg.percent.toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}

            <div className="mt-2 p-3 rounded-md !bg-primary/10 dark:!bg-primary/20 border border-primary/20 dark:border-primary/30">
              <p className="text-xs !text-gray-600 dark:!text-dark-text-secondary !mb-1">
                {t('dashboard.total_investment_value')}
              </p>
              <p className="text-xl font-bold !text-primary dark:!text-primary !mb-0">
                {formatCurrency(totalValue)} VND
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
